import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Show } from "shared/components";
import { Switch, SwitchSize } from "shared/components/forms/Switch";
import { SectionCard } from "shared/app/SectionCard";
import { FraudAlertRow } from "shared/app/FraudAlertRow";
import { PoweredByPropertyShield } from "shared/app/PoweredByPropertyShield";
import { useFraudAlertsController } from "shared/queries/useFraudAlertsController";
import {
  FRAUD_ALERTS_KEY,
  MONITORED_PROPERTIES_KEY,
  MONITORING_STATUS_KEY,
  propertyShieldApi
} from "api/propertyShield";
import { MonitoringStatus } from "./MonitoringStatus";
import addressApi from "api/landlord/address";
import { Property } from "types/landlord/address";
import { getErrorMessage } from "utils/error";

const RECENT_ALERTS_LIMIT = 10;

export const ListingFraudAlertsSection = ({
  property
}: {
  property: Property;
}) => {
  const queryClient = useQueryClient();
  const propertyQueryKey = ["properties", String(property.id)];
  const enabled = property.property_shield_monitoring_enabled ?? false;

  const { alerts, total, isLoading } = useFraudAlertsController(
    { property_id: property.id, per_page: RECENT_ALERTS_LIMIT },
    { enabled }
  );
  const { data: monitoringStatus } = useQuery({
    queryKey: [MONITORING_STATUS_KEY, property.id],
    queryFn: () => propertyShieldApi.getMonitoringStatus(property.id),
    enabled
  });
  // Past alerts stay visible; only the empty state waits for a unit Property Shield can watch.
  const canMonitor = (monitoringStatus?.monitored_count ?? 0) > 0;

  const { mutate: toggleMonitoring, isPending: isToggling } = useMutation({
    mutationFn: (value: boolean) =>
      addressApi.editProperty({
        propertyId: property.id,
        property_shield_monitoring_enabled: value
      }),
    onSuccess: (_, value) => {
      queryClient.setQueryData<Property>(propertyQueryKey, (old) =>
        old ? { ...old, property_shield_monitoring_enabled: value } : old
      );
      queryClient.invalidateQueries({ queryKey: propertyQueryKey });
      queryClient.invalidateQueries({ queryKey: [MONITORED_PROPERTIES_KEY] });
      queryClient.invalidateQueries({ queryKey: [FRAUD_ALERTS_KEY] });
      queryClient.invalidateQueries({ queryKey: [MONITORING_STATUS_KEY] });
    },
    onError: (error) => {
      toast.error(
        `Failed to update listing fraud monitoring: ${getErrorMessage(error)}`
      );
    }
  });

  return (
    <SectionCard title="Fraudulent listing monitoring" noPadding>
      <div className="px-8 py-6">
        <div className="flex items-center gap-3">
          <Switch
            checked={enabled}
            size={SwitchSize.BASE}
            disabled={isToggling}
            onChange={(v) => toggleMonitoring(v)}
          />
          <p className="text-sm font-medium text-content-700">
            Monitor this property for fraudulent listings
          </p>
        </div>
        <Show if={enabled && !!monitoringStatus}>
          <div className="ml-[52px] mt-2.5">
            {monitoringStatus ? (
              <MonitoringStatus
                propertyId={property.id}
                status={monitoringStatus}
              />
            ) : null}
          </div>
        </Show>
      </div>

      <Show if={enabled && !isLoading && (alerts.length > 0 || canMonitor)}>
        <div className="px-8 py-6 border-t border-alpha-40">
          {alerts.length > 0 ? (
            <>
              <ul className="space-y-3">
                {alerts.map((alert) => (
                  <FraudAlertRow key={alert.id} alert={alert} showUnit />
                ))}
              </ul>
              <Show if={total > alerts.length}>
                <Link
                  to={`/listing-fraud-alerts?property_id=${property.id}`}
                  className="mt-3 block text-right text-sm text-primary-500 hover:text-primary-600"
                >
                  View all {total}
                </Link>
              </Show>
            </>
          ) : (
            <p className="py-2 text-center text-sm text-content-400">
              No alerts. Property Shield will let you know if a copy of this
              listing turns up.
            </p>
          )}
        </div>
      </Show>

      <PoweredByPropertyShield
        className="px-8 py-4 bg-alpha-20 border-t border-alpha-40 rounded-b-lg"
        description="Property Shield watches other platforms for fraudulent copies of this listing."
      />
    </SectionCard>
  );
};
